import React from 'react';
import { motion } from 'framer-motion';
import { useGame } from '../context/GameContext';
import { safePreventDefault } from '../utils/browserCompat';
import { WordPosition } from '../types/game';

interface WordCircleProps {
  word: string;
  position: WordPosition;
  radius: number;
  fontSize: number;
  isSelected: boolean;
}

const WordCircle: React.FC<WordCircleProps> = ({
  word,
  position,
  radius,
  fontSize,
  isSelected,
}) => {
  const { actions, gameState } = useGame();

  const isSelectable = actions.isWordSelectable(word);

  const variants = {
    hidden: { scale: 1, y: 0, x: 0 },
    jump: {
      y: [0, -15, 0],
      scale: [1, 1.05, 1],
      transition: { duration: 0.3, ease: "easeInOut" as const }
    },
    shake: {
      x: [0, -5, 5, -5, 5, 0],
      transition: { duration: 0.4, ease: "easeInOut" as const }
    }
  };

  const animation = actions.getAnimation ? actions.getAnimation(word) : 'hidden';

  const userCorrectConnections = gameState.connections.filter(conn => 
    !conn.isRevealed && conn.isCorrect && (conn.from === word || conn.to === word)
  ).length;
  const isCompleted = userCorrectConnections >= 2;
  
  // During solution animation, preserve the user's progress colors
  const shouldShowUserProgress = gameState.showingSolution && gameState.errors >= gameState.maxErrors;

  const handleClick = () => {
    if (!isSelectable) return;

    if (gameState.selectedWord && gameState.selectedWord !== word) {
      actions.validateConnection(gameState.selectedWord, word);
    } else {
      actions.selectWord(isSelected ? null : word);
    }
  };

  const handleMouseDown = (event: React.MouseEvent) => {
    if (!gameState.dragMode || !isSelectable) return;
    
    actions.selectWord(word);
    actions.setDragging(true);
    
    safePreventDefault(event.nativeEvent);
    event.stopPropagation();
  };

  const handleMouseUp = (event: React.MouseEvent) => {
    if (!gameState.dragMode) return;
    
    if (gameState.isDragging && gameState.selectedWord && gameState.selectedWord !== word) {
      if (!isSelectable) {
        actions.setDragging(false);
        return;
      }
      
      actions.validateConnection(gameState.selectedWord, word);
      actions.setDragging(false);
    } else if (gameState.isDragging) {
      actions.setDragging(false);
    }
    
    event.stopPropagation();
  };

  const handleTouchStart = (event: React.TouchEvent) => {
    if (!gameState.dragMode || !isSelectable) return;
    
    try {
      actions.selectWord(word);
      actions.setDragging(true);
      
      safePreventDefault(event.nativeEvent);
      event.stopPropagation();
    } catch (error) {
      console.warn('Touch start error:', error);
      if (!gameState.selectedWord) {
        actions.selectWord(word);
      }
    }
  };

  const handleMouseEnter = () => {
    if (!gameState.dragMode || !isSelectable) return;
    
    if (gameState.isDragging && gameState.selectedWord && gameState.selectedWord !== word) {
      actions.setHoveredWord(word);
    }
  };

  const handleMouseLeave = () => {
    if (!gameState.dragMode) return;
    
    if (gameState.isDragging && gameState.hoveredWord === word) {
      actions.setHoveredWord(null);
    }
  };

  const handleClickMode = (event: React.MouseEvent) => {
    if (gameState.dragMode || !isSelectable) return;
    
    if (gameState.selectedWord && gameState.selectedWord !== word) {
      actions.validateConnection(gameState.selectedWord, word);
    } else {
      actions.selectWord(isSelected ? null : word);
    }
    
    event.stopPropagation();
  };
  
  const isHoveredTarget = gameState.dragMode && gameState.hoveredWord === word && gameState.isDragging;
  const isInteractionTarget = isSelected || isHoveredTarget || gameState.secondSelectedWord === word;
  
  return (
    <motion.g
      variants={variants}
      animate={animation}
    >
      <circle
        cx={position.x}
        cy={position.y}
        r={radius}
        fill={
          (isCompleted || (shouldShowUserProgress && isCompleted))
            ? "#10b981" 
            : isHoveredTarget
              ? "#dbeafe" 
            : isInteractionTarget 
              ? "#3b82f6" 
              : "#ffffff"
        }
        stroke={
          (isCompleted || (shouldShowUserProgress && isCompleted))
            ? "#059669" 
            : isHoveredTarget
              ? "#3b82f6" 
            : isInteractionTarget 
              ? "#1d4ed8" 
              : "#d1d5db"
        }
        strokeWidth={isHoveredTarget ? 4 : (isInteractionTarget ? 3 : 2)}
        style={{
          cursor: isSelectable ? (isSelected ? 'grab' : 'pointer') : 'default',
          opacity: isSelectable ? 1 : 0.6,
          transition: 'opacity 0.2s ease-in-out, fill 0.2s ease-in-out',
        }}
        onMouseDown={gameState.dragMode ? handleMouseDown : undefined}
        onMouseUp={gameState.dragMode ? handleMouseUp : undefined}
        onMouseEnter={gameState.dragMode ? handleMouseEnter : undefined}
        onMouseLeave={gameState.dragMode ? handleMouseLeave : undefined}
        onTouchStart={gameState.dragMode ? handleTouchStart : undefined}
        onClick={gameState.dragMode ? handleClick : handleClickMode}
        data-word={word}
      />
      
      <text
        x={position.x}
        y={position.y}
        textAnchor="middle"
        dominantBaseline="central"
        fontSize={fontSize}
        fontWeight="600"
        fill={
          (isCompleted || (shouldShowUserProgress && isCompleted))
            ? "white" 
            : isHoveredTarget
              ? "#1d4ed8" 
            : isInteractionTarget 
              ? "white" 
              : "#374151"
        }
        style={{ 
          userSelect: 'none',
          pointerEvents: 'none',
        }}
      >
        {word.toUpperCase()}
      </text>
    </motion.g>
  );
};

export default WordCircle;